import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  InformationCircleIcon,
  TicketIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import orderApi from '../../services/orderApi';
import LogoBlack from '../../assets/logo-black.png';
import LogoWhite from '../../assets/logo-white-brand.svg';
import NoStoreLogo from '../../assets/no-store-logo.svg';
import Visa from '../../assets/visa.svg';
import MasterCard from '../../assets/mastercard.svg';
import AmericanExpress from '../../assets/american-express.svg';
import Discover from '../../assets/discover.svg';
import CreditCardSvg from '../../assets/credit-card-icon.svg';
import StoreMap from './components/StoreMap';
import TaxFeesDialog from '../orders/order-detail/components/tax-fees-dialog';
import ReceiptSkeleton from './components/receipt-skeleton';
import Rating from './components/Rating';
import { parseStars } from './stars';
import { TWButton } from '../../components/ui';
import {
  formatOptionName,
  getExtendedOptionPrice,
} from '../../utils/optionDisplay';
import { inMenuOrder, optionsInMenuOrder } from '../../utils/menu-order';
import {
  hasTaxFeesBreakdown as hasTaxFeesBreakdownFor,
  taxAndFeesTotal,
  extraFeeRows,
} from '../../utils/orderTotals';

import '../orders/order-detail/order-detail.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const DISPLAY_FONT = '"Bricolage Grotesque", "Lato", system-ui, sans-serif';

const usd = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

const cardBrandImage = (brand) => {
  const b = String(brand || '').toLowerCase();
  if (b.includes('visa')) return Visa;
  if (b.includes('master')) return MasterCard;
  if (b.includes('amex') || b.includes('american')) return AmericanExpress;
  if (b.includes('discover')) return Discover;
  return CreditCardSvg;
};

const getCardInfo = (orderDetail) => {
  const payment = orderDetail?.payments?.[0] || {};
  const brand =
    orderDetail?.cardbrand ||
    orderDetail?.brand ||
    payment?.cardbrand ||
    payment?.brand ||
    payment?.card?.brand ||
    payment?.paymentmethoddetails?.card?.brand;
  const last4 =
    orderDetail?.cardlast4 ||
    orderDetail?.last4 ||
    payment?.cardlast4 ||
    payment?.last4 ||
    payment?.card?.last4 ||
    payment?.paymentmethoddetails?.card?.last4;
  return { brand, last4 };
};

const TotalRow = ({ label, value, accent, accentColor, valueColor }) => (
  <div className="flex items-center justify-between py-1.5">
    <span
      className={
        accent
          ? 'text-base font-bold text-slate-900 dark:text-slate-100'
          : 'text-sm text-slate-500 dark:text-slate-400'
      }
    >
      {label}
    </span>
    <span
      className={
        'tabular-nums ' +
        (accent
          ? 'text-base font-black text-slate-900 dark:text-slate-100'
          : 'text-sm font-semibold ' +
            (valueColor ? '' : 'text-slate-700 dark:text-slate-200'))
      }
      style={
        accentColor
          ? { color: accentColor }
          : valueColor
          ? { color: valueColor }
          : undefined
      }
    >
      {value}
    </span>
  </div>
);

const Receipt = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const initialStars = parseStars(searchParams.get('stars'));
  const [orderDetail, setOrderDetail] = useState({});
  const [loading, setLoading] = useState({ initialLoading: true });
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);

  const getOrderDetail = async () => {
    try {
      setLoading((prev) => ({ ...prev, initialLoading: true }));
      const res = await orderApi.getInvoiceById(params.id);
      if (res.data?.data) {
        setOrderDetail(res.data.data);
      }
    } catch (error) {
      // swallow
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  const handleAppStoreClick = (e, storeUrl, deeplinkUrl, androidIntentUrl) => {
    e.preventDefault();
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = deeplinkUrl;
      document.body.appendChild(iframe);
      const timer = setTimeout(() => {
        document.body.removeChild(iframe);
        window.location.href = storeUrl;
      }, 1500);
      window.addEventListener('blur', () => {
        clearTimeout(timer);
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 100);
      });
      return;
    }
    if (/android/i.test(userAgent)) {
      window.location.href = androidIntentUrl || storeUrl;
      return;
    }
    window.open(storeUrl, '_blank');
  };

  useEffect(() => {
    getOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  if (loading.initialLoading) {
    return <ReceiptSkeleton />;
  }

  if (!orderDetail.store) {
    return (
      <div className="order-shell mx-auto max-w-xl space-y-4 bg-white p-6 dark:bg-slate-900/40">
        <SkeletonBlock className="mx-auto h-10 w-32" />
        <SkeletonBlock className="h-4 w-48" />
        <SkeletonBlock className="h-40 w-full" />
      </div>
    );
  }

  const store = orderDetail.store;
  const storeBase = `${process.env.REACT_APP_ORDER_URL}/${store.slug}`;
  const sitecolor = store.sitecolor || '#0f172a';

  const finalTotal = orderDetail.refund
    ? orderDetail.total - orderDetail.refund
    : orderDetail.total;

  const taxAndFees = taxAndFeesTotal(orderDetail);
  const hasTaxFeesBreakdown = hasTaxFeesBreakdownFor(orderDetail);

  const { brand, last4 } = getCardInfo(orderDetail);
  const hasCard = Boolean(brand || last4);

  const paidAt = store.timezoneid
    ? dayjs(orderDetail.createdate)
        .tz(store.timezoneid)
        .format('MMM D, YYYY [·] h:mm A')
    : dayjs(orderDetail.createdate)
        .utc()
        .utcOffset((orderDetail.timezone || 0) * 60)
        .format('MMM D, YYYY [·] h:mm A');

  const addressLine = [
    store.address,
    store.apartment && store.apartment !== 'null' ? store.apartment : null,
  ]
    .filter(Boolean)
    .join(' ');

  const orderTotalDigits = usd(finalTotal).replace('$', '');

  // SVG grain pattern (subtle warmth on hero)
  const grainBg =
    'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")';

  return (
    <div className="order-shell min-h-screen bg-white dark:bg-slate-950">
      {/* Top bar — thisway logo (left) + Login (right) */}
      <header className="reveal relative z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
        <Link to={storeBase} className="inline-flex">
          <img className="h-7 w-auto dark:hidden" src={LogoBlack} alt="Thisway" />
          <img className="hidden h-7 w-auto dark:block" src={LogoWhite} alt="Thisway" />
        </Link>
        <Link
          to={`${storeBase}/login`}
          className="text-sm font-semibold text-slate-700 hover:text-blue-600 dark:text-slate-200 dark:hover:text-sky-400"
        >
          Login
        </Link>
      </header>

      {/* Receipt card */}
      <main className="relative z-10 mx-auto max-w-md px-4 py-8 sm:py-12">
        <article className="reveal relative overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-900/[0.06] ring-1 ring-slate-200 dark:bg-slate-900 dark:shadow-black/40 dark:ring-slate-800">
          {/* HERO band — plain colored band, no text */}
          <section
            className="relative h-24 overflow-hidden"
            style={{ backgroundColor: sitecolor }}
            aria-label="Store color band"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
              style={{ backgroundImage: grainBg }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-white/15 blur-3xl"
            />
          </section>

          {/* Logo — centered, straddling the band/body boundary */}
          <div className="relative h-0">
            <img
              src={store.storelogo || NoStoreLogo}
              alt={store.name}
              className="absolute left-1/2 -top-10 h-20 w-20 -translate-x-1/2 rounded-xl bg-white object-cover shadow-xl ring-4 ring-white"
            />
          </div>

          {/* Body */}
          <div className="px-6 pb-7 pt-14 sm:px-8">
            {/* Store info — centered, refined */}
            <section className="text-center">
              <h1
                className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100"
                style={{ fontFamily: DISPLAY_FONT }}
              >
                {store.name}
              </h1>
              {addressLine && (
                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                  <MapPinIcon className="h-3.5 w-3.5 text-slate-400" />
                  <span>{addressLine}</span>
                </p>
              )}
              {(store.city || store.state || store.postalcode) && (
                <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                  {[store.city, store.state, store.postalcode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              {store.phone && (
                <a
                  href={`tel:${store.phone}`}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline dark:text-sky-400"
                >
                  <PhoneIcon className="h-3.5 w-3.5" />
                  {store.phone}
                </a>
              )}
            </section>

            {/* Big Total — display font, with $ above the digits */}
            <section className="relative mt-7 text-center">
              {/* Dashed perforation */}
              <div className="absolute inset-x-0 -top-2 border-t border-dashed border-slate-300 dark:border-slate-700" />
              <p className="mt-5 text-[0.65rem] font-bold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">
                Total
              </p>
              <div
                className="mt-1 flex items-start justify-center gap-1"
                style={{ fontFamily: DISPLAY_FONT }}
              >
                <span className="mt-3 text-2xl font-bold text-slate-700 dark:text-slate-200">
                  $
                </span>
                <span className="text-7xl font-extrabold tabular-nums leading-none text-slate-900 dark:text-slate-100">
                  {orderTotalDigits}
                </span>
              </div>
              <p className="mt-2 text-[0.7rem] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                Order #{orderDetail.orderid}
                {orderDetail.ordernumberstr && (
                  <span
                    className="ml-2 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-white"
                    style={{ backgroundColor: sitecolor }}
                  >
                    {orderDetail.ordernumberstr}
                  </span>
                )}
              </p>
            </section>

            {/* Pickup time */}
            {orderDetail.pickuptime && (
              <section className="mt-6 rounded-md bg-amber-50/70 px-4 py-2.5 text-center text-xs text-amber-800 dark:bg-amber-900/15 dark:text-amber-300">
                <span className="font-bold uppercase tracking-wider">Pickup</span>{' '}
                ·{' '}
                {store.timezoneid
                  ? dayjs(orderDetail.pickuptime)
                      .tz(store.timezoneid)
                      .format('ddd, MMM D [·] h:mm A')
                  : dayjs(orderDetail.pickuptime)
                      .utc()
                      .utcOffset((orderDetail.timezone || 0) * 60)
                      .format('ddd, MMM D [·] h:mm A')}
              </section>
            )}

            {/* Perforated separator */}
            <div className="my-6 border-t border-dashed border-slate-300 dark:border-slate-700" />

            {/* Items */}
            <section>
              <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-[0.25em] text-slate-400 dark:text-slate-500">
                Order Items
              </p>
              <div className="space-y-4">
                {inMenuOrder(orderDetail.orderitems).map((item, i) => {
                  const product = item.item?.product;
                  const hasVariant = product?.name !== item.name;
                  const discounted =
                    Number(item.originaltotal || 0) > Number(item.total || 0);

                  return (
                    <div key={i} className="group">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <span className="flex-shrink-0 text-sm font-bold tabular-nums text-slate-500 dark:text-slate-400">
                            {item.quantity}×
                          </span>
                          <h4
                            className="text-base font-bold text-slate-900 dark:text-slate-100"
                            style={{ fontFamily: DISPLAY_FONT }}
                          >
                            {product?.name}
                          </h4>
                        </div>
                        <div className="text-right">
                          {discounted ? (
                            <>
                              <p className="tabular-nums text-base font-bold text-emerald-600 dark:text-emerald-400">
                                {usd(item.total)}
                              </p>
                              <p className="tabular-nums text-xs text-slate-400 line-through">
                                {usd(item.originaltotal)}
                              </p>
                            </>
                          ) : (
                            <p
                              className="tabular-nums text-base font-bold text-slate-900 dark:text-slate-100"
                              style={{ fontFamily: DISPLAY_FONT }}
                            >
                              {usd(item.total)}
                            </p>
                          )}
                        </div>
                      </div>
                      {hasVariant && (
                        <p className="ml-9 mt-1 text-xs italic text-slate-600 dark:text-slate-300">
                          {item.name}
                          {item.price + (orderDetail.itemfee || 0) > 0 && (
                            <span className="ml-1.5 not-italic font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                              · {usd(item.price + (orderDetail.itemfee || 0))}
                            </span>
                          )}
                        </p>
                      )}
                      {item.orderitemoptions && item.orderitemoptions.length > 0 && (
                        <ul className="ml-9 mt-1.5 space-y-0.5">
                          {optionsInMenuOrder(item.orderitemoptions)
                            .map((opt, k) => (
                              <li
                                key={k}
                                className="flex items-baseline gap-1.5 text-xs text-slate-500 dark:text-slate-400"
                              >
                                <span className="text-slate-300 dark:text-slate-600">
                                  +
                                </span>
                                <span className="truncate">
                                  {formatOptionName(opt, item.quantity)}
                                </span>
                                {opt.price > 0 && (
                                  <span className="italic font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                                    {usd(
                                      getExtendedOptionPrice(opt, item.quantity),
                                    )}
                                  </span>
                                )}
                              </li>
                            ))}
                        </ul>
                      )}
                      {item.note && (
                        <p className="ml-9 mt-1.5 rounded-md bg-slate-50 px-2.5 py-1 text-xs italic text-slate-500 dark:bg-slate-800/70 dark:text-slate-400">
                          “{item.note}”
                        </p>
                      )}
                      {(item.coupontypelabel === 'Buy X Get Y' ||
                        item.coupontypelabel === 'Spend Get Free Item') && (
                        <div className="ml-9 mt-2">
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                            <TicketIcon className="h-3 w-3" />
                            {item.coupontypelabel === 'Buy X Get Y'
                              ? `Buy ${item.buyItemCount}, Get ${item.getItemCount} Free`
                              : 'Free Item'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Perforated separator */}
            <div className="my-6 border-t border-dashed border-slate-300 dark:border-slate-700" />

            {/* Totals — clean rows */}
            <section>
              <TotalRow label="Subtotal" value={usd(orderDetail.subtotal)} />
              <div className="flex items-center justify-between py-1.5">
                <button
                  type="button"
                  onClick={
                    hasTaxFeesBreakdown ? () => setTaxDialogOpen(true) : undefined
                  }
                  className={
                    'flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400' +
                    (hasTaxFeesBreakdown
                      ? ' hover:text-blue-600 dark:hover:text-sky-400'
                      : '')
                  }
                >
                  {hasTaxFeesBreakdown ? 'Sales Tax & Fees' : 'Sales Tax'}
                  {hasTaxFeesBreakdown && (
                    <InformationCircleIcon className="h-3.5 w-3.5" />
                  )}
                </button>
                <span className="tabular-nums text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {usd(taxAndFees)}
                </span>
              </div>
              {extraFeeRows(orderDetail).map((fee, i) => (
                <TotalRow key={i} label={fee.name} value={usd(fee.price)} />
              ))}
              {orderDetail.tip !== 0 && (
                <TotalRow label="Tip" value={usd(orderDetail.tip)} />
              )}
              {orderDetail.discount !== 0 && (
                <div className="flex items-center justify-between py-1.5">
                  <span className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
                    <TicketIcon className="h-3.5 w-3.5" />
                    <span>Discount</span>
                    {orderDetail.discountcode && (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-mono text-[0.6rem] font-bold uppercase tracking-wider text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                        {orderDetail.discountcode}
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                    − {usd(orderDetail.discount)}
                  </span>
                </div>
              )}
              {orderDetail.refund !== 0 && (
                <TotalRow
                  label="Refund"
                  value={`− ${usd(orderDetail.refund)}`}
                  valueColor="#e11d48"
                />
              )}
              <div className="mt-3 border-t border-slate-300 pt-3 dark:border-slate-700">
                <TotalRow label="Total" value={usd(finalTotal)} accent />
              </div>
            </section>

            {/* Perforated separator */}
            <div className="my-7 border-t border-dashed border-slate-300 dark:border-slate-700" />

            {/* Payment — stamp-like Paid + datetime */}
            <section className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {hasCard ? (
                  <>
                    <img
                      src={cardBrandImage(brand)}
                      alt={brand || 'Card'}
                      className="h-8 w-auto"
                    />
                    <div>
                      <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        {brand || 'Card'}
                      </p>
                      {last4 && (
                        <p className="font-mono tabular-nums text-xs text-slate-500 dark:text-slate-400">
                          ···· {last4}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    Paid
                  </div>
                )}
              </div>
              {/* PAID stamp */}
              <div
                className="-rotate-6 select-none rounded-md border-2 px-3 py-1 font-mono text-[10px] font-black uppercase tracking-[0.25em]"
                style={{
                  borderColor: '#16a34a',
                  color: '#16a34a',
                  boxShadow: 'inset 0 0 0 1px rgba(22, 163, 74, 0.15)',
                }}
              >
                Paid
              </div>
            </section>
            <p className="mt-2 text-right text-[0.7rem] text-slate-500 dark:text-slate-400">
              {paidAt}
            </p>

            {/* Map */}
            {store.latitude && store.longitude ? (
              <section className="mt-7">
                <StoreMap lat={store.latitude} lng={store.longitude} />
              </section>
            ) : null}

            {/* Rating — elevated, inviting */}
            <section className="mt-7 rounded-lg border border-slate-200 bg-white px-5 py-7 text-center dark:border-slate-700 dark:bg-slate-900">
              <Rating order={orderDetail} color={sitecolor} initialRating={initialStars} />
            </section>
          </div>
        </article>

        {/* Actions — outside the receipt card */}
        <div className="reveal mt-8 flex flex-col gap-3">
          <TWButton
            size="lg"
            variant="primary"
            block
            label="Order Again"
            onClick={() => window.location.replace(storeBase)}
          />
          <TWButton
            size="lg"
            block
            label="My Account"
            onClick={() => window.location.replace(storeBase)}
          />
        </div>

        {/* Thank you + app stores */}
        <div className="reveal mt-10 space-y-4 pb-2 text-center">
          <p
            className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100"
            style={{ fontFamily: DISPLAY_FONT }}
          >
            Thank You
          </p>
          <div className="flex justify-center gap-3">
            <a
              href="https://apps.apple.com/us/app/thisway-co/id6740181672"
              onClick={(e) =>
                handleAppStoreClick(
                  e,
                  'itms-apps://itunes.apple.com/app/id6740181672',
                  'thisway://open',
                  null,
                )
              }
              target="_blank"
              rel="noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                alt="Thisway iOS app"
                src="https://cdn.prod.website-files.com/64bf0dc67f53cb40f4a3f4de/68894d12a8d89cdb4e9a3e8d_App%20Store%20Button.svg"
                className="h-8 w-auto"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.thisway.customerapp.customerapp"
              onClick={(e) =>
                handleAppStoreClick(
                  e,
                  'https://play.google.com/store/apps/details?id=com.thisway.customerapp.customerapp',
                  'thisway://open',
                  'intent://open#Intent;scheme=thisway;package=com.thisway.customerapp.customerapp;end',
                )
              }
              target="_blank"
              rel="noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                alt="Thisway Android app"
                src="https://cdn.prod.website-files.com/64bf0dc67f53cb40f4a3f4de/68894d12a8d89cdb4e9a3e86_Google%20Play%20Button.svg"
                className="h-8 w-auto"
              />
            </a>
          </div>
          <div className="flex justify-center pt-2">
            <a
              href="https://thisway.co"
              className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400"
            >
              <span>Powered by</span>
              <img
                className="h-4 w-auto pl-1 dark:hidden"
                src={LogoBlack}
                alt="Thisway"
              />
              <img
                className="hidden h-4 w-auto pl-1 dark:block"
                src={LogoWhite}
                alt="Thisway"
              />
            </a>
          </div>
        </div>

        <TaxFeesDialog
          open={taxDialogOpen}
          onClose={() => setTaxDialogOpen(false)}
          orderDetail={orderDetail}
        />
      </main>
    </div>
  );
};

export default Receipt;
